import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
  SimpleChanges
} from '@angular/core';
import { AbstractControl, UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { get as _get, has as _has } from 'lodash';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, map, takeUntil } from 'rxjs/operators';

import { APP_STATE } from '../../+state/app-state/app-state.models';
import { Prediction } from '../../+state/geo-coder/geo-coder.models';
import {
  DEFAULT_LOCATION_DISTANCE,
  SearchContext,
  SearchedValue
} from '../../+state/service-locator/service-locator.models';
import { Distance, DISTANCES, STATE_CODES } from '../../models/forms.models';
import { AutofillDropdownService } from '../../services/autofill-dropdown.service';
import { ValidationService } from '../../services/validation.service';

@Component({
  selector: 'drp-search-by-location-form',
  templateUrl: './search-by-location-form.component.html',
  styleUrls: ['./search-by-location-form.component.scss'],
  standalone: false
})
export class SearchByLocationFormComponent implements OnInit, OnChanges {
  /** List of predictions from Geo API based on what user has typed */
  @Input() predictions: Array<Prediction>;

  /** address returned from geo details from associated placeid
   * based on what the user had selected from the dropdown  -used
   * to populate the form
   */
  @Input() addressToPopulate: any;

  /** Current App State */
  @Input() appState: string;

  /** Current map event that would trigger a redraw */
  @Input() mapTriggerState: string;

  /** Input Search Value that is coming from the parent component */
  @Input() inputSearchValue: SearchedValue;

  /** Address on submit */
  @Output() searchedAddress = new EventEmitter<any>();

  /** updates any time user changes any form inputs */
  @Output() requestAutocompleteEvent = new EventEmitter<any>();

  /** Address from from dropdown that user has selected - emits a place_id
   * to get the address details from Geo API
   */
  @Output() userSelectedAddress = new EventEmitter<any>();

  searchByLocationForm: UntypedFormGroup;
  submitted = false;

  states: Array<string>;
  distances: Array<Distance>;

  // Used to turn on / off value change listening
  private stopValueListener$;

  constructor(
    private formBuilder: UntypedFormBuilder,
    private validationService: ValidationService,
    private dropdown: AutofillDropdownService
  ) {}

  ngOnInit() {
    this.states = STATE_CODES;
    this.distances = DISTANCES;
    this.setupForm();
    this.dropdown.hide();
  }

  ngOnChanges(changes: SimpleChanges) {
    // eslint-disable-next-line guard-for-in
    for (const propName in changes) {
      const change = changes[propName];
      if (!change.isFirstChange()) {
        if (propName === 'addressToPopulate') {
          this.fillFromPredictedAddress();
        }
        if (propName === 'appState' && this.appState === APP_STATE.NO_ZIP_FOUND) {
          const distance = this.searchByLocationForm.controls['distance'].value;
          this.searchByLocationForm.reset();
          this.searchByLocationForm.get('distance').setValue(distance);
        }
      }
    }
  }

  /**
   * Pass as output the address the user selected from the autocomplete dropdown
   * @param address
   */
  onPredictionSelect(prediction: Prediction) {
    this.userSelectedAddress.emit(prediction);
  }

  /**
   * Emit the address at the time the user hits the search button
   * Only will emit if valid
   */
  submitSearchByLocation() {
    this.submitted = true;
    if (this.searchByLocationForm.valid) {
      this.searchedAddress.emit(this.searchByLocationForm.value);
      this.submitted = false;
    }
  }

  /**
   * Initialize Form
   */
  private setupForm() {
    this.searchByLocationForm = this.formBuilder.group({
      line1: [_get(this.inputSearchValue, 'line1', '')],
      city: [_get(this.inputSearchValue, 'city', '')],
      state: [_get(this.inputSearchValue, 'state', '')],
      zip: [
        _get(this.inputSearchValue, 'zip', ''),
        [Validators.required, Validators.maxLength(5), ValidationService.zipCodeNumbersAndLength],
        this.validateZipCodeExists.bind(this)
      ],
      distance: [DEFAULT_LOCATION_DISTANCE]
    });

    this.attachChangeListener();
  }

  private fillFromPredictedAddress() {
    const line1 = this.addressToPopulate.address_line1
      ? this.addressToPopulate.address_line1
      : this.addressToPopulate.formatted_address.split(',')[0];
    const city = this.addressToPopulate.city ? this.addressToPopulate.city : '';
    const state = this.addressToPopulate.stateCode ? this.addressToPopulate.stateCode : '';
    const zip = this.addressToPopulate.zip ? this.addressToPopulate.zip : '';

    // hide the dropdown values
    this.dropdown.hide();

    // terminate the value listener
    this.detachChangeListener();

    // Update the form values
    this.searchByLocationForm.setValue({
      line1: line1,
      city: city,
      state: state,
      zip: zip,
      distance: this.searchByLocationForm.controls['distance'].value
    });

    // Re-enable the form listener
    this.attachChangeListener();
  }

  private attachChangeListener() {
    this.stopValueListener$ = new Subject<void>();
    this.searchByLocationForm.controls.line1.valueChanges
      .pipe(
        // Stop subject will terminate the value change listener subscription on selection of a predicted value
        takeUntil(this.stopValueListener$),
        // debounce input for 100 milliseconds
        debounceTime(500),
        // only emit if emission is different from previous emission
        distinctUntilChanged()
      )
      .subscribe(value => {
        if (value && value !== '') {
          this.requestAutocompleteEvent.emit(value);
        } else {
          // hide the dropdown results
          this.dropdown.hide();
        }
      });
  }

  private detachChangeListener() {
    this.stopValueListener$.next();
    this.stopValueListener$.complete();
  }

  /**
   * Async validation to check the existence of the zip code entered
   */
  private validateZipCodeExists(control: AbstractControl) {
    return this.validationService.zipExists(control.value, SearchContext.DRP_SHOP_SEARCH).pipe(
      map(res => {
        if (res) {
          return res.exactAddressMatchIndicator ? null : { zipcodeNotRecognized: true };
        } else {
          return { zipcodeNotRecognized: true };
        }
      })
    );
  }
}
